/**
 * CUSTOMER FIX Stage 2 / Home v1.3 W2 — nearby location disclosure BottomSheet.
 * GPS is requested only when the user taps "use my location" inside this sheet.
 * H05/F05 — "Zadat město / PSČ / oblast" uses searchNominatimPlaces (no GPS).
 */

import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PLACE_SEARCH_EMPTY_TEST_ID,
  PLACE_SEARCH_MULTI_HIT_TEST_ID,
  searchNominatimPlaces,
  type NominatimPlaceHit,
} from '../features/account/mapViewsNominatim.js';
import { coarsePlaceLabelFromDisplayName } from '../shared/ui/directoryGeoPlace.js';
import { useOptionalOverlayBackDismiss } from '../shared/context/OverlayBackDismissContext.js';
import { BottomSheet } from '../shared/ui/BottomSheet.js';
import { Button } from '../shared/ui/Button.js';
import type {
  DiscoveryManualPlaceSelection,
  DiscoveryShellLocationApi,
} from './DiscoveryShellLocationContext.js';

export interface CustomerLocationDisclosureSheetProps
  extends Pick<
    DiscoveryShellLocationApi,
    'nearbyActive' | 'geoPending' | 'geoErrorKey' | 'radiusMeters'
  > {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly requestNearby: () => void;
  readonly applyManualPlace: (place: DiscoveryManualPlaceSelection) => void;
  readonly clearNearbyContext: () => void;
}

function formatRadiusKm(radiusMeters: number): number {
  return Math.round(radiusMeters / 1000);
}

type PlaceSearchStatus = 'idle' | 'loading' | 'empty' | 'hits' | 'error';

export function CustomerLocationDisclosureSheet({
  open,
  onClose,
  nearbyActive,
  geoPending,
  geoErrorKey,
  radiusMeters,
  requestNearby,
  applyManualPlace,
  clearNearbyContext,
}: CustomerLocationDisclosureSheetProps): JSX.Element {
  const { t } = useTranslation('customer');
  const titleId = useId();
  const descriptionId = useId();
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;
  const [mode, setMode] = useState<'choose' | 'manualPlace'>('choose');
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeHits, setPlaceHits] = useState<readonly NominatimPlaceHit[]>([]);
  const [placeSearchStatus, setPlaceSearchStatus] =
    useState<PlaceSearchStatus>('idle');

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  useEffect(() => {
    if (open) {
      return;
    }
    // queueMicrotask: avoid react-hooks/set-state-in-effect (cascading render)
    queueMicrotask(() => {
      setMode('choose');
      setPlaceQuery('');
      setPlaceHits([]);
      setPlaceSearchStatus('idle');
    });
  }, [open]);

  const title =
    mode === 'manualPlace'
      ? t('browse.discovery.enterCityPostalOrArea')
      : t('browse.discovery.locationDisclosureTitle');
  const radiusKm =
    nearbyActive && radiusMeters !== null ? formatRadiusKm(radiusMeters) : null;

  const runPlaceSearch = useCallback(
    async (event: FormEvent): Promise<void> => {
      event.preventDefault();
      const q = placeQuery.trim();
      if (q.length === 0) {
        setPlaceHits([]);
        setPlaceSearchStatus('empty');
        return;
      }
      setPlaceSearchStatus('loading');
      const result = await searchNominatimPlaces(q);
      if (result.kind === 'error') {
        setPlaceHits([]);
        setPlaceSearchStatus('error');
        return;
      }
      if (result.kind === 'empty') {
        setPlaceHits([]);
        setPlaceSearchStatus('empty');
        return;
      }
      setPlaceHits(result.hits);
      setPlaceSearchStatus('hits');
    },
    [placeQuery],
  );

  const handlePickHit = useCallback(
    (hit: NominatimPlaceHit): void => {
      const label = coarsePlaceLabelFromDisplayName(hit.displayName);
      applyManualPlace({ lat: hit.lat, lng: hit.lng, label });
      onClose();
    },
    [applyManualPlace, onClose],
  );

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title}
      titleId={titleId}
      describedBy={descriptionId}
      closeLabel={t('shared.cancel')}
      testId="customer-location-disclosure-sheet"
      className="max-h-[90vh] bg-[var(--color-surface-elevated)] p-5 lg:max-w-lg"
      containerClassName="lg:items-center"
    >
      <div
        className="flex flex-col gap-4"
        data-testid="customer-location-disclosure-body"
        data-location-sheet-mode={mode}
      >
        {mode === 'choose' ? (
          <>
            {nearbyActive && radiusKm !== null ? (
              <p
                id={descriptionId}
                className="text-sm text-[var(--color-on-surface-muted)]"
                data-testid="customer-location-disclosure-active-hint"
              >
                {t('browse.discovery.locationActiveHint', { km: radiusKm })}
              </p>
            ) : (
              <p
                id={descriptionId}
                className="text-sm text-[var(--color-on-surface-muted)]"
              >
                {t('browse.discovery.discoveryLandingBody')}
              </p>
            )}

            {geoErrorKey !== null ? (
              <p
                className="text-sm text-[var(--color-on-surface)]"
                data-testid={`customer-location-disclosure-geo-${geoErrorKey}`}
                role="alert"
              >
                {t(
                  geoErrorKey === 'denied'
                    ? 'browse.discovery.locationDenied'
                    : 'browse.discovery.locationUnavailable',
                )}
              </p>
            ) : null}

            <div className="flex flex-col gap-2">
              <Button
                type="button"
                intent="primary"
                size="lg"
                block
                disabled={geoPending}
                data-testid="customer-location-disclosure-use-my-location"
                onClick={() => {
                  requestNearby();
                  onClose();
                }}
              >
                {geoPending
                  ? t('browse.discovery.locating')
                  : t('browse.discovery.useMyLocation')}
              </Button>

              <Button
                type="button"
                intent="secondary"
                size="lg"
                block
                disabled={geoPending}
                data-testid="customer-location-disclosure-enter-place"
                onClick={() => {
                  setMode('manualPlace');
                  setPlaceSearchStatus('idle');
                  setPlaceHits([]);
                }}
              >
                {t('browse.discovery.enterCityPostalOrArea')}
              </Button>

              {nearbyActive ? (
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  block
                  disabled={geoPending}
                  data-testid="customer-location-disclosure-clear-nearby"
                  onClick={() => {
                    clearNearbyContext();
                    onClose();
                  }}
                >
                  {t('browse.discovery.clearNearbyLocation')}
                </Button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <p
              id={descriptionId}
              className="text-sm text-[var(--color-on-surface-muted)]"
            >
              {t('browse.discovery.placeSearchPick')}
            </p>

            <form
              className="flex flex-col gap-2"
              onSubmit={(event) => {
                void runPlaceSearch(event);
              }}
              data-testid="customer-location-disclosure-place-form"
            >
              <label className="flex min-w-0 flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]">
                <span>{t('browse.discovery.placeSearchPlaceholder')}</span>
                <input
                  type="search"
                  value={placeQuery}
                  autoComplete="off"
                  data-testid="customer-location-disclosure-place-search"
                  className="min-h-11 w-full rounded-sm border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-sm text-[var(--color-on-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
                  onChange={(event) => {
                    setPlaceQuery(event.target.value);
                  }}
                />
              </label>
              <Button
                intent="secondary"
                type="submit"
                size="md"
                block
                loading={placeSearchStatus === 'loading'}
                data-testid="customer-location-disclosure-place-search-submit"
              >
                {placeSearchStatus === 'loading'
                  ? t('browse.discovery.placeSearchLoading')
                  : t('browse.discovery.placeSearchSubmit')}
              </Button>
            </form>

            {placeSearchStatus === 'empty' ? (
              <p
                className="text-sm text-[var(--color-on-surface-muted)]"
                data-testid={PLACE_SEARCH_EMPTY_TEST_ID}
                role="status"
              >
                {t('browse.discovery.placeSearchEmpty')}
              </p>
            ) : null}

            {placeSearchStatus === 'error' ? (
              <p
                className="text-sm text-[var(--color-danger)]"
                data-testid="customer-location-disclosure-place-search-error"
                role="alert"
              >
                {t('browse.discovery.placeSearchError')}
              </p>
            ) : null}

            {placeSearchStatus === 'hits' ? (
              <div className="flex flex-col gap-2">
                {placeHits.length > 1 ? (
                  <p
                    className="text-sm text-[var(--color-on-surface-muted)]"
                    data-testid={PLACE_SEARCH_MULTI_HIT_TEST_ID}
                    role="status"
                  >
                    {t('browse.discovery.placeSearchMultiHit')}
                  </p>
                ) : null}
                <ul
                  className="flex max-h-56 flex-col gap-1 overflow-y-auto"
                  data-testid="customer-location-disclosure-place-hits"
                >
                  {placeHits.map((hit, index) => (
                    <li key={`${hit.displayName}-${String(hit.placeId ?? index)}`}>
                      <button
                        type="button"
                        className="flex min-h-11 w-full rounded-md border border-[var(--color-border)] px-3 py-2 text-left text-sm text-[var(--color-on-surface)] hover:bg-[var(--color-surface-muted)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
                        data-testid={`customer-location-disclosure-place-hit-${String(index)}`}
                        onClick={() => {
                          handlePickHit(hit);
                        }}
                      >
                        {hit.displayName}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <Button
              type="button"
              intent="ghost"
              size="md"
              block
              data-testid="customer-location-disclosure-place-back"
              onClick={() => {
                setMode('choose');
                setPlaceHits([]);
                setPlaceSearchStatus('idle');
              }}
            >
              {t('browse.discovery.placeSearchBack')}
            </Button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
